<template>
  <div class="rendering-engines flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        Rendering Engines
        <a-tag color="orange" class="staff-gate-tag">staff</a-tag>
      </span>
    </div>

    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="loading">
      <Spinner />
    </div>

    <div v-else class="a-body flex-grow-1 d-flex flex-column overflow-y-hidden p-20-px">
      <a-alert
        v-if="!isStaff"
        type="warning"
        show-icon
        message="The engine catalog is Cloudsine staff only."
      />

      <div v-else class="engines-layout">
        <div class="engines-list">
          <div class="engines-list__head">
            <a-input-search v-model="search" placeholder="Search engines" allow-clear />
            <a-button type="dashed" icon="plus" block class="mt-2" @click="startCreate">
              Add engine
            </a-button>
          </div>

          <div class="engines-list__items">
            <div
              v-if="creating"
              class="engine-item engine-item--active"
              @click="startCreate"
            >
              <div class="engine-item__label">New engine</div>
              <div class="engine-item__code">Not saved yet</div>
            </div>

            <div
              v-for="engine in filteredEngines"
              :key="engine.engine_code"
              class="engine-item"
              :class="{ 'engine-item--active': engine.engine_code === selectedCode }"
              @click="select(engine)"
            >
              <div class="engine-item__label">
                {{ engine.engine_label || engine.engine_code }}
              </div>
              <div class="engine-item__code">{{ engine.engine_code }}</div>
              <a-tag class="engine-item__kind">{{ engine.engine_kind }}</a-tag>
            </div>

            <a-empty
              v-if="!filteredEngines.length && !creating"
              :image="simpleEmptyImage"
              description="No engines match."
            />
          </div>
        </div>

        <div class="engine-detail">
          <a-empty
            v-if="!editing"
            :image="simpleEmptyImage"
            description="Select an engine to edit its defaults."
          />

          <template v-else>
            <div class="engine-detail__head">
              <div class="engine-detail__title">
                {{ creating ? 'New engine' : form.engine_code }}
              </div>
              <div class="engine-detail__actions">
                <a-button :disabled="saving" @click="revert">Reset</a-button>
                <a-button type="primary" :loading="saving" @click="save">Save</a-button>
                <a-button
                  v-if="!creating"
                  type="danger"
                  ghost
                  :loading="deletingCode === selectedCode"
                  @click="remove"
                >
                  Delete
                </a-button>
              </div>
            </div>

            <div class="engine-detail__body">
              <div class="config-section">
                <div class="config-section__title">Engine</div>
                <div class="config-grid config-grid--3">
                  <div class="config-field">
                    <div class="config-field__label">Engine code</div>
                    <a-input
                      v-model="form.engine_code"
                      :disabled="!creating"
                      placeholder="e.g. curl_only"
                    />
                  </div>
                  <div class="config-field">
                    <div class="config-field__label">Label</div>
                    <a-input v-model="form.engine_label" />
                  </div>
                  <div class="config-field">
                    <div class="config-field__label">Kind</div>
                    <a-select v-model="form.engine_kind" style="width: 100%">
                      <a-select-option v-for="kind in engineKinds" :key="kind" :value="kind">
                        {{ kind }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </div>

              <div v-for="group in groups" :key="group.name" class="config-section">
                <div class="config-section__title">{{ group.title }}</div>
                <div class="config-section__hint">{{ group.hint }}</div>

                <div v-if="group.fields.length" class="config-grid config-grid--3">
                  <RenderingConfigField
                    v-for="field in group.fields"
                    :key="field.key"
                    :field="field"
                    v-model="values[field.key]"
                    :user-agents="userAgents"
                  >
                    <template #label-extra>
                      <a-button
                        type="link"
                        size="small"
                        class="config-field__remove"
                        title="Remove this value"
                        @click="removeField(field.key)"
                      >
                        Remove
                      </a-button>
                    </template>
                  </RenderingConfigField>
                </div>

                <div v-else class="config-section__empty">No values set.</div>

                <div class="config-section__add">
                  <a-select
                    :value="undefined"
                    show-search
                    option-filter-prop="children"
                    placeholder="Add field"
                    style="width: 260px"
                    :disabled="!group.available.length"
                    @change="addField"
                  >
                    <a-select-option
                      v-for="field in group.available"
                      :key="field.key"
                      :value="field.key"
                    >
                      {{ field.label }}
                    </a-select-option>
                  </a-select>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Empty } from 'ant-design-vue';
  import RenderingService from '@modules/Policy/services/rendering.service';
  import RenderingConfigField from '@modules/Policy/components/RenderingConfigField.vue';
  import renderingAccess from '@modules/Policy/mixins/renderingAccess.mixin';
  import {
    ENGINE_FIELD_GROUPS,
    ENGINE_KINDS,
    configToForm,
    engineFieldFor,
    formToConfig,
    getApiErrorMessage,
  } from '@modules/Policy/consts/RenderingConfig.consts';
  import '@modules/Policy/styles/rendering-config.css';

  const emptyForm = () => ({
    engine_code: '',
    engine_label: '',
    engine_kind: 'html',
  });

  export default {
    name: 'RenderingEnginesPage',
    components: { RenderingConfigField },
    mixins: [renderingAccess],
    data() {
      return {
        simpleEmptyImage: Empty.PRESENTED_IMAGE_SIMPLE,
        engineKinds: ENGINE_KINDS,
        loading: false,
        saving: false,
        deletingCode: null,
        engines: [],
        search: '',
        selectedCode: null,
        creating: false,
        form: emptyForm(),
        /** Keys held by the selected engine, in the order the editor shows them. */
        activeKeys: [],
        values: {},
        /** Stored keys outside the editor catalog. Kept so a save cannot drop them. */
        extraConfig: {},
        /** The saved state, used to detect edits and to reset the form. */
        baseline: null,
      };
    },
    computed: {
      editing() {
        return this.creating || Boolean(this.selectedCode);
      },
      filteredEngines() {
        const term = this.search.trim().toLowerCase();
        const list = this.engines.slice().sort((a, b) =>
          String(a.engine_code).localeCompare(String(b.engine_code))
        );
        if (!term) {
          return list;
        }
        return list.filter((engine) =>
          `${engine.engine_code} ${engine.engine_label} ${engine.engine_kind}`
            .toLowerCase()
            .includes(term)
        );
      },
      /** Each group splits into the fields already set and the fields still available. */
      groups() {
        return ENGINE_FIELD_GROUPS.map((group) => ({
          name: group.name,
          title: group.title,
          hint: group.hint,
          fields: group.fields.filter((field) => this.activeKeys.indexOf(field.key) !== -1),
          available: group.fields.filter((field) => this.activeKeys.indexOf(field.key) === -1),
        }));
      },
      activeFields() {
        return this.activeKeys.map((key) => engineFieldFor(key)).filter(Boolean);
      },
      snapshot() {
        return JSON.stringify({
          code: this.form.engine_code,
          label: this.form.engine_label,
          kind: this.form.engine_kind,
          config: this.buildConfig(),
        });
      },
      dirty() {
        return this.baseline !== null && this.snapshot !== this.baseline;
      },
    },
    async mounted() {
      await this.load();
    },
    methods: {
      async load() {
        if (this.loading) {
          return;
        }
        this.loading = true;
        try {
          if (!this.isStaff) {
            this.engines = [];
            return;
          }
          const [packed] = await Promise.all([
            RenderingService.listEngines(this.engineCacheKey),
            this.loadUserAgents({ silent: true }),
          ]);
          this.engines = Array.isArray(packed)
            ? packed
            : ((packed && packed.engines) || []);
          this.restoreSelection();
        } catch (e) {
          this.engines = [];
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load engines.'));
        } finally {
          this.loading = false;
        }
      },
      /** Keep the open engine after a reload, else open the first one. */
      restoreSelection() {
        if (this.creating) {
          return;
        }
        const current = this.engines.find((engine) => engine.engine_code === this.selectedCode);
        const next = current || this.engines[0];
        if (next) {
          this.open(next);
        }
      },
      select(engine) {
        if (engine.engine_code === this.selectedCode && !this.creating) {
          return;
        }
        this.guardUnsaved(() => this.open(engine));
      },
      startCreate() {
        this.guardUnsaved(() => this.resetCreateForm());
      },
      open(engine) {
        this.creating = false;
        this.selectedCode = engine.engine_code;
        this.form = {
          engine_code: engine.engine_code,
          engine_label: engine.engine_label || '',
          engine_kind: engine.engine_kind || 'html',
        };
        const config = engine.default_config || {};
        const known = Object.keys(config).filter((key) => engineFieldFor(key));
        const extra = {};
        Object.keys(config).forEach((key) => {
          if (known.indexOf(key) === -1) {
            extra[key] = config[key];
          }
        });
        this.activeKeys = known;
        this.extraConfig = extra;
        this.values = configToForm(config, this.activeFields);
        this.baseline = this.snapshot;
      },
      guardUnsaved(next) {
        if (!this.dirty) {
          next();
          return;
        }
        this.$confirm({
          title: 'Discard unsaved changes?',
          content: 'The values you changed on this engine are not saved.',
          onOk: next,
        });
      },
      revert() {
        if (this.creating) {
          this.resetCreateForm();
          return;
        }
        const engine = this.engines.find((item) => item.engine_code === this.selectedCode);
        if (engine) {
          this.open(engine);
        }
      },
      resetCreateForm() {
        this.creating = true;
        this.selectedCode = null;
        this.form = emptyForm();
        this.activeKeys = [];
        this.values = {};
        this.extraConfig = {};
        this.baseline = this.snapshot;
      },
      addField(key) {
        if (!key || this.activeKeys.indexOf(key) !== -1) {
          return;
        }
        const field = engineFieldFor(key);
        if (!field) {
          return;
        }
        this.activeKeys = this.activeKeys.concat(key);
        this.$set(this.values, key, configToForm({}, [field])[key]);
      },
      removeField(key) {
        this.activeKeys = this.activeKeys.filter((item) => item !== key);
        this.$delete(this.values, key);
      },
      buildConfig() {
        return Object.assign({}, this.extraConfig, formToConfig(this.values, this.activeFields));
      },
      async save() {
        if (this.saving) {
          return;
        }
        if (!this.form.engine_code || !this.form.engine_label || !this.form.engine_kind) {
          this.$showErrorMessage('Engine code, label and kind are required.');
          return;
        }
        const default_config = this.buildConfig();
        this.saving = true;
        try {
          if (this.creating) {
            await RenderingService.createEngine({
              engine_code: this.form.engine_code,
              engine_label: this.form.engine_label,
              engine_kind: this.form.engine_kind,
              default_config,
            });
            this.selectedCode = this.form.engine_code;
            this.creating = false;
            this.$showSuccessMessage('Engine created.');
          } else {
            await RenderingService.updateEngine(this.selectedCode, {
              engine_label: this.form.engine_label,
              engine_kind: this.form.engine_kind,
              default_config,
            });
            this.$showSuccessMessage('Engine updated.');
          }
          this.baseline = this.snapshot;
          await this.load();
        } catch (e) {
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to save engine.'));
        } finally {
          this.saving = false;
        }
      },
      remove() {
        if (this.saving || this.deletingCode || !this.selectedCode) {
          return;
        }
        const code = this.selectedCode;
        this.$confirm({
          title: `Delete engine ${code}?`,
          content: 'URLs that still select this engine may fail resolve until updated.',
          onOk: async () => {
            this.deletingCode = code;
            try {
              await RenderingService.deleteEngine(code);
              this.selectedCode = null;
              this.baseline = null;
              this.$showSuccessMessage('Engine deleted.');
              await this.load();
            } catch (e) {
              this.$showErrorMessage(getApiErrorMessage(e, 'Failed to delete engine.'));
            } finally {
              this.deletingCode = null;
            }
          },
        });
      },
    },
  };
</script>

<style scoped>
  .staff-gate-tag {
    margin-left: 8px;
    font-size: 12px;
    line-height: 18px;
    height: 20px;
    vertical-align: middle;
  }
  .engines-layout {
    display: flex;
    gap: 16px;
    flex: 1 1 auto;
    min-height: 0;
  }
  .engines-list {
    display: flex;
    flex-direction: column;
    width: 300px;
    flex: 0 0 300px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }
  .engines-list__head {
    padding: 12px;
    border-bottom: 1px solid #e8e8e8;
  }
  .engines-list__items {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 8px;
  }
  .engine-item {
    position: relative;
    padding: 8px 70px 8px 12px;
    border-radius: 4px;
    cursor: pointer;
  }
  .engine-item:hover {
    background: #f5f5f5;
  }
  .engine-item--active {
    background: #e6f7ff;
  }
  .engine-item__label {
    font-weight: 600;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .engine-item__code {
    font-size: 12px;
    color: #8c8c8c;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .engine-item__kind {
    position: absolute;
    top: 10px;
    right: 8px;
    margin: 0;
  }
  .engine-detail {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }
  .engine-detail__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border-bottom: 1px solid #e8e8e8;
  }
  .engine-detail__title {
    font-size: 16px;
    font-weight: 600;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .engine-detail__actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }
  .engine-detail__body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 20px;
  }
  .engine-detail__body .config-section {
    margin-bottom: 16px;
  }
</style>
